import {
  Component,
  ViewEncapsulation,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { getCardOptions } from 'src/old-pages/booking/confirm/confirm-card-options';
import { Card } from 'src/providers/card/card';
import { StripeProvider } from 'src/providers/stripe/stripe-provider';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Loading } from 'src/shared/components/loading/loading';
import { Util } from 'src/shared/util/util';
import {
  BANK_MICRODEPOSITS_MESSAGE,
  apiErrorMessage,
  bankStepProblemMessage,
  bankStepRefusalMessage,
  waitsForMicrodeposits,
} from 'src/shared/util/bank-account-setup';
import { WindowService } from 'src/shared/providers/window.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PaymentMethodsProvider } from 'src/providers/payment-methods/payment-methods.provider';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { ThemeService } from 'src/shared/services/theme.service';
import { CheckPaymentMethodData } from 'src/models/vendor.model';

@Component({
    templateUrl: 'payment-method-form.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PaymentMethodFormPage implements AfterViewInit, OnInit, OnDestroy {
  backPage: string;
  cameFromAutomaticBooking: boolean;
  cameFromSendPayment: boolean;
  cardBeingEditted: any;
  @ViewChild('cardInfo', { static: false }) cardInfo: ElementRef;
  @ViewChild('bankInfo', { static: false }) bankInfo: ElementRef;
  cardEl: any;
  cardHandler = this.onChange.bind(this);
  dialogParams: any;
  errorMessage: String;
  hasValidCaptcha: string | null = null;
  isPrimaryCard: boolean;
  showDefaultCardCheckbox: boolean;
  loaded: boolean;
  stripeReady = false;
  teamId: any;
  isRightSideContent = true;
  cardOrBankItems: any[] = [
    { viewValue: 'Card', value: 'card' },
    { viewValue: 'Bank Account', value: 'bank' },
    { viewValue: 'Check (for vendor invoices)', value: 'check' },
  ];
  chooseOrAddCardItems: any[] = [
    { viewValue: 'Add a new card', value: 'add' },
    { viewValue: 'Choose a card', value: 'choose' },
  ];
  bankCountryItems: any[] = [
    { viewValue: 'United States', value: 'US' },
    { viewValue: 'Canada', value: 'CA' },
  ];
  form = new FormGroup({
    cardOrBank: new FormControl('card'),
    chooseOrAddCard: new FormControl('add'),
    paymentMethod: new FormControl(null),
    name: new FormControl(''),
    check_bank_name: new FormControl(''),
    check_account_number: new FormControl(''),
    check_routing_number: new FormControl(''),
    check_bank_country_code: new FormControl('US'),
    check_contact_name: new FormControl(''),
    check_contact_address1: new FormControl(''),
    check_contact_address2: new FormControl(''),
    check_contact_city: new FormControl(''),
    check_contact_state: new FormControl(''),
    check_contact_postal: new FormControl(''),
    check_contact_country: new FormControl('US'),
  });
  isCardSelected = true;
  paymentMethodsItems: any[] = [];
  isAddMissingBankInfo: boolean;
  submitted = false;
  signatureDataUrl = '';

  boundSubmitCard = this.submitCard.bind(this);
  boundNextForBankAccount = this.nextForBankAccount.bind(this);
  boundSubmitCheck = this.submitCheck.bind(this);

constructor(
    private cd: ChangeDetectorRef,
    private cardService: Card,
    private stripeProvider: StripeProvider,
    private navCtrl: CustomNavController,
    private util: Util,
    private windowService: WindowService,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private paymentMethodsProvider: PaymentMethodsProvider,
    private themeService: ThemeService,
  ) {}

  @Loading('', true)
  async ngOnInit(): Promise<void> {
    try {
      this.loaded = false;
      this.initVariables();
      this.loaded = true;
    } catch (err) {
      this.errorMessage =
        err.error && err.error.message ? err.error.message : err.message;
    }
  }

  ngAfterViewInit(): void {
    this.initiateCardElement();
  }

  ngOnDestroy(): void {
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      this.cardEl.destroy();
      this.cardEl = null;
    }
  }

  async initVariables(): Promise<void> {
    this.isRightSideContent =
      (await this.storage.retrieve('dialog-right-side-open')) || false;
    if (this.isRightSideContent) {
      this.dialogParams = await this.storage.retrieve('dialog-params');
    }
    this.cardBeingEditted = this.navCtrl.getParam('card');
    const cards = this.navCtrl.getParam('cards') || this.dialogParams?.cards;
    this.teamId = this.navCtrl.getParam('teamId') || this.dialogParams?.teamId;
    this.showDefaultCardCheckbox =
      this.teamId || (cards?.length === 1 && this.cardBeingEditted)
        ? false
        : true;
    this.isPrimaryCard =
      this.teamId || !this.cardBeingEditted
        ? false
        : this.cardBeingEditted?.is_primary;
    this.cameFromSendPayment =
      this.navCtrl.getParam('cameFromSendPayment') ||
      this.dialogParams?.cameFromSendPayment;
    this.cameFromAutomaticBooking =
      this.navCtrl.getParam('cameFromAutomaticBooking') ||
      this.dialogParams?.cameFromAutomaticBooking;
    this.backPage =
      this.navCtrl.getParam('backPage') ||
      this.dialogParams?.backPage ||
      'more';
    this.rightSidePanelService.setDialogPageTitle(
      this.cardBeingEditted ? 'Edit Payment Method' : 'Add Payment Method'
    );
    this.isAddMissingBankInfo =
      this.dialogParams?.action === 'addMissingBankInfo' ||
      this.navCtrl.getParam('action') === 'addMissingBankInfo';
    if (this.isAddMissingBankInfo) {
      this.form.controls.cardOrBank.setValue('bank');
      this.isCardSelected = false;
    }
    let paymentMethods = await this.paymentMethodsProvider.getPaymentMethods();
    paymentMethods = this.util.parsePaymentMethods(paymentMethods);
    this.paymentMethodsItems = paymentMethods
      .map((paymentMethod) => ({
        ...paymentMethod,
        viewValue: this.getPaymentMethodLabel(paymentMethod),
        value: paymentMethod.id,
        type: paymentMethod.type,
        icon:
          paymentMethod.type === 'card'
            ? 'assets/svg/card.svg'
            : 'assets/img/bank.png',
      }))
      .filter(
        (paymentMethod) =>
          paymentMethod.status === 'succeeded' &&
          (this.teamId ? paymentMethod.type === 'card' : false)
      );
  }

  getPaymentMethodLabel(paymentMethod: any) {
    let label = paymentMethod.type === 'card' ? 'Card' : 'Bank Account';
    if (paymentMethod?.metadata?.last4) {
      label += ` ${new TranslationPipe().transform('ending in')} ${paymentMethod.metadata.last4}`;
    }
    if (paymentMethod?.is_prmary) {
      label += ` ${new TranslationPipe().transform('(primary)')}`;
    }
    return label;
  }

  selectChooseOrAddCard(event: {
    viewValue: string;
    value: 'choose' | 'add';
  }): void {
    if (event.value === 'choose') {
      this.isCardSelected = false;
      if (this.cardEl) {
        this.cardEl.removeEventListener('change', this.cardHandler);
        this.cardEl.destroy();
        this.cardEl = null;
      }
    } else {
      this.isCardSelected = true;
      this.initiateCardElement();
    }
  }

  selectCardOrBank(event: { viewValue: string; value: 'card' | 'bank' | 'check' }): void {
    if (event.value === 'card') {
      this.isCardSelected = true;
      this.initiateCardElement();
      return;
    }
    this.isCardSelected = false;
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      this.cardEl.destroy();
      this.cardEl = null;
    }
  }

  onSignatureChange(dataUrl: string) {
    this.signatureDataUrl = dataUrl;
  }

  async submitCheck(): Promise<void> {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.hasValidCaptcha) {
      this.errorMessage = 'Please check that you are not a robot.';
      return;
    }
    const v = this.form.value;
    const missingBase = !v.check_bank_name || !v.check_account_number || !v.check_bank_country_code;
    const isUS = v.check_bank_country_code === 'US';
    const missingRouting = isUS ? !v.check_routing_number : !v.check_routing_number;
    const missingContact = !v.check_contact_name || !v.check_contact_address1 || !v.check_contact_city ||
      !v.check_contact_state || !v.check_contact_postal || !v.check_contact_country;
    if (missingBase || missingRouting || missingContact || !this.signatureDataUrl) {
      this.errorMessage = 'Please complete all required check fields and provide a signature.';
      return;
    }
    const data: CheckPaymentMethodData = {
      bank_name: v.check_bank_name!,
      account_number: v.check_account_number!,
      bank_country_code: v.check_bank_country_code!,
      signature_text: this.signatureDataUrl,
      contact: {
        name: v.check_contact_name!,
        address_line1: v.check_contact_address1!,
        address_line2: v.check_contact_address2 || undefined,
        city: v.check_contact_city!,
        province_or_state: v.check_contact_state!,
        postal_or_zip: v.check_contact_postal!,
        country_code: v.check_contact_country!,
      },
    };
    if (isUS) {
      data.routing_number = v.check_routing_number!;
    } else {
      data.transit_number = v.check_routing_number!;
    }
    try {
      await this.paymentMethodsProvider.addCheckPaymentMethod(data);
      await this.paymentMethodsProvider.getPaymentMethods(true);
      this.util.showSuccess('Check payment method added.');
      this.navBack();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to add check payment method.';
    }
  }

  initiateCardElement(): void {
    this.stripeReady = false;
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      this.cardEl.destroy();
      this.cardEl = null;
    }
    setTimeout(() => {
      if (!this.cardInfo?.nativeElement) {
        return;
      }
      this.cardEl = this.stripeProvider
        .getElements()
        .create('card', getCardOptions(!this.themeService.isDarkMode()));
      this.cardEl.mount(this.cardInfo.nativeElement);
      this.cardEl.addEventListener('change', this.cardHandler);
      this.cardEl.on('ready', () => {
        this.stripeReady = true;
        this.cd.detectChanges();
      });
    }, 100);
  }

  onChange(err: any): void {
    this.errorMessage = err.error ? err.error.message : err.message;
    this.cd.detectChanges();
  }

  async handleChooseCard(): Promise<void> {
    if (!this.form.value.paymentMethod) {
      this.errorMessage = 'Please select a card.';
    } else {
      try {
        const payload = {
          account_team_id: this.teamId,
          payment_method_id: this.form.value.paymentMethod,
        };
        await this.paymentMethodsProvider.associatePaymentMethodWithTeam(
          this.teamId,
          payload
        );
        this.util.showSuccess('Card added to team successfully.');
        this.navBack();
      } catch (_err) {
        this.errorMessage = 'Error adding card to team.';
        this.util.showError(this.errorMessage as string);
      }
    }
  }

  async submitCard(): Promise<void> {
    this.errorMessage = '';
    if (!this.hasValidCaptcha) {
      this.errorMessage = 'Please check that you are not a robot.';
      return;
    }
    if (this.teamId && this.form.value.chooseOrAddCard === 'choose') {
      await this.handleChooseCard();
      return;
    }
    const { token, error } = await this.stripeProvider
      .getInstance()
      .createToken(this.cardEl);
    if (error) {
      this.setErrorCard(error);
    } else {
      await this.checkCard(token);
    }
  }

  async checkCard(token: any): Promise<void> {
    try {
      const paymentMethod: any = await this.cardService.saveCard({
        type: 'card',
        stripe_card_token: token.id,
        primary_payment_method: this.isPrimaryCard,
      });
      if (this.teamId) {
        const payload = {
          account_team_id: this.teamId,
          payment_method_id: paymentMethod.id,
        };
        await this.paymentMethodsProvider.associatePaymentMethodWithTeam(
          this.teamId,
          payload
        );
      }
      if (this.cardBeingEditted) {
        await this.paymentMethodsProvider.removePaymentMethod(
          this.cardBeingEditted.id
        );
      }
      await this.paymentMethodsProvider.getPaymentMethods(true);
      this.goToNextPage();
    } catch (err) {
      this.setErrorCard(err);
    }
  }

  captchaEvents(captchaResult): void {
    this.hasValidCaptcha = captchaResult;
  }

  goToNextPage(): void {
    const goToMyProsAfter =
      this.navCtrl.getParam('goToMyProsAfter') ||
      this.dialogParams?.goToMyProsAfter;
    let url = '';
    let params = {};
    if (this.teamId) {
      this.util.showSuccess('Card added to team successfully.');
      this.navBack();
      return;
    } else if (goToMyProsAfter) {
      const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
      url = 'success';
      params = {
        header: 'Payment Method Added',
        body: "You've enabled auto-schedule and added a payment method. We will now send job requests to your Pro priority list as new events are synced. You can add any existing Pro to your Pro priority list if you want them to accept jobs (working with existing Pros is free).",
        buttonText: 'Review Pro Priority List',
        buttonRoute:
          isRentalClient && this.windowService.isDesktopRes
            ? 'contacts'
            : 'job-request-workflows',
      };
    } else if (this.cameFromSendPayment) {
      url = 'get-started-send-payment';
      params = {
        pro: this.navCtrl.getParam('pro') || this.dialogParams?.pro,
        creditBalance:
          this.navCtrl.getParam('creditBalance') ||
          this.dialogParams?.creditBalance,
        address: this.navCtrl.getParam('address') || this.dialogParams?.address,
        allowsInvoiceAutopay:
          this.navCtrl.getParam('allowsInvoiceAutopay') ||
          this.dialogParams?.allowsInvoiceAutopay,
      };
    } else {
      url = 'success';
      params = {
        header: this.cardBeingEditted
          ? 'Credit Card Edited'
          : 'Credit Card Added',
        body: '',
        buttonText: 'Ok',
        buttonRoute: 'more'
      };
    }
    this.rightSidePanelService.navigateTo(url, params);
  }

  setErrorCard(err: any): void {
    if (err.code === 402) {
      this.errorMessage =
        'Your credit card was declined. Please double-check and try again.';
    } else {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  goToContactConcierge(): void {
    const params = {
      title: 'Integrations Help',
      type: 'support.integration',
    };
    this.rightSidePanelService.navigateTo('contact-concierge', params);
  }

  goToScheduleCall(): void {
    const url = 'https://www.tidy.com/schedule-integration-support';
    this.util.openUrl(url);
  }

  navBack(): void {
    const isDesktopRes = this.windowService.isDesktopRes;
    if (isDesktopRes) {
      this.rightSidePanelService.goBack();
    } else {
      this.navCtrl.navigateRoot('payment-methods');
    }
  }

  async handleAddMissingBankInfo(name: string): Promise<void> {
    try {
      const response = await this.stripeProvider.collectBankAccountForSetup({
        name,
        client_secret:
          this.dialogParams?.client_secret ||
          (await this.navCtrl.getParam('client_secret')),
      });
      const bankStepProblem = bankStepProblemMessage(response);
      if (bankStepProblem) {
        this.util.showWarning(bankStepProblem);
        return;
      }
      this.util.showSuccess('Bank account added successfully.');
      this.navBack();
    } catch (err) {
      this.util.showError(`Error adding bank account. ${  err.message}`);
    }
  }

  async nextForBankAccount(): Promise<void> {
    if (!this.hasValidCaptcha) {
      this.errorMessage = 'Please check that you are not a robot.';
      return;
    }
    try {
      const { name } = this.form.value;
      if (this.isAddMissingBankInfo) {
        this.handleAddMissingBankInfo(name);
        return;
      }
      const response = await this.paymentMethodsProvider.addBankAccount();
      const clientSecret = response.metadata.stripe.client_secret;
      const bankStepResult = await this.stripeProvider.collectBankAccountForSetup({
        name,
        client_secret: clientSecret,
      });
      const bankStepProblem = bankStepProblemMessage(bankStepResult);
      if (bankStepProblem) {
        this.util.showWarning(bankStepProblem);
        return;
      }
      await this.confirmPaymentMethod(response.id);
    } catch (err) {
      this.util.showError(new TranslationPipe().transform('Error adding bank account. ') + apiErrorMessage(err));
    }
  }

  async confirmPaymentMethod(paymentMethodId: string) {
    const loading = await this.util.showLoading();
    try {
      const confirmedPaymentMethod = await this.paymentMethodsProvider.confirmPaymentMethod(paymentMethodId);
      if (waitsForMicrodeposits(confirmedPaymentMethod)) {
        this.util.showWarning(BANK_MICRODEPOSITS_MESSAGE, 10000);
      } else {
        this.util.showSuccess('Bank account confirmed successfully.');
      }
      const goToMyProsAfter = this.navCtrl.getParam('goToMyProsAfter') || this.dialogParams?.goToMyProsAfter;
      if (goToMyProsAfter) {
        const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
        const params = {
          header: 'Payment Method Added',
          body: "You've enabled auto-schedule and added a payment method. We will now send job requests to your Pro priority list as new events are synced. You can add any existing Pro to your Pro priority list if you want them to accept jobs (working with existing Pros is free).",
          buttonText: 'Review Pro Priority List',
          buttonRoute: isRentalClient && this.windowService.isDesktopRes ? 'contacts' : 'job-request-workflows',
        };
        this.rightSidePanelService.navigateTo('success', params);
      } else {
        this.navBack();
      }
    } catch (confirmError) {
      const bankStepRefusal = bankStepRefusalMessage(confirmError);
      if (bankStepRefusal) {
        this.util.showWarning(bankStepRefusal);
        return;
      }
      this.util.showError(
        new TranslationPipe().transform('Error confirming bank account. ') + apiErrorMessage(confirmError)
      );
    } finally {
      loading.dismiss();
    }
  }
}
